import { Box, ColorInput, Group, Slider, Stack, Text, Tooltip, CopyButton, ActionIcon } from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { buildGradientString, parseGradientString, DEFAULT_GRADIENT_FROM, DEFAULT_GRADIENT_TO, DEFAULT_GRADIENT_DEG } from "./gradientUtils";

interface GradientInputProps {
    value?: string | null;
    onChange?: (value: string) => void;
    label?: string;
    description?: string;
    error?: React.ReactNode;
}

export function GradientInput({ value, onChange, label, description, error }: GradientInputProps) {
    const parsed = useMemo(() => parseGradientString(value), [value]);

    const [fromColor, setFromColor] = useState(parsed.from);
    const [toColor, setToColor] = useState(parsed.to);
    const [deg, setDeg] = useState(parsed.deg);

    useEffect(() => {
        setFromColor(parsed.from);
        setToColor(parsed.to);
        setDeg(parsed.deg);
    }, [parsed.from, parsed.to, parsed.deg]);

    useEffect(() => {
        if (!value) {
            const initial = buildGradientString(DEFAULT_GRADIENT_FROM, DEFAULT_GRADIENT_TO, DEFAULT_GRADIENT_DEG);
            onChange?.(initial);
        }
    }, [value, onChange]);

    const updateGradient = (newFrom: string, newTo: string, newDeg: number) => {
        const result = buildGradientString(newFrom, newTo, newDeg);
        onChange?.(result);
    };

    const handleFromChange = (c: string) => {
        const val = c || "#ffffff";
        setFromColor(val);
        updateGradient(val, toColor, deg);
    };

    const handleToChange = (c: string) => {
        const val = c || "#ffffff";
        setToColor(val);
        updateGradient(fromColor, val, deg);
    };

    const handleDegChange = (d: number) => {
        setDeg(d);
        updateGradient(fromColor, toColor, d);
    };

    const currentGradient = buildGradientString(fromColor, toColor, deg);

    return (
        <Stack gap="xs">
            {label && (
                <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>{label}</Text>
                    <CopyButton value={currentGradient} timeout={2000}>
                        {({ copied, copy }) => (
                            <Tooltip label={copied ? "已複製 CSS" : "複製 CSS 漸層語法"} withArrow position="left">
                                <ActionIcon color={copied ? "teal" : "gray"} variant="subtle" size="xs" onClick={copy}>
                                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </CopyButton>
                </Group>
            )}

            {description && <Text size="xs" c="dimmed">{description}</Text>}

            {/* 即時預覽色塊 */}
            <Box
                h={38}
                style={{
                    background: currentGradient,
                    borderRadius: "var(--mantine-radius-md)",
                    border: "1px solid var(--mantine-color-default-border)",
                    boxShadow: "inset 0 0 4px rgba(0,0,0,0.08)"
                }}
            />

            {/* 起訖色彩選擇 */}
            <Group grow align="flex-start">
                <ColorInput
                    size="xs"
                    label="起始顏色"
                    value={fromColor ?? ''}
                    onChange={handleFromChange}
                />
                <ColorInput
                    size="xs"
                    label="結束顏色"
                    value={toColor ?? ''}
                    onChange={handleToChange}
                />
            </Group>

            {/* 角度滑桿 */}
            <Stack gap={4} mt={4}>
                <Group justify="space-between" align="center">
                    <Text size="xs" c="dimmed">漸層角度</Text>
                    <Text size="xs" fw={500}>{deg}°</Text>
                </Group>
                <Slider
                    size="xs"
                    min={0}
                    max={360}
                    step={5}
                    value={deg}
                    onChange={handleDegChange}
                    marks={[
                        { value: 0, label: "0°" },
                        { value: 90, label: "90°" },
                        { value: 180, label: "180°" },
                        { value: 270, label: "270°" },
                        { value: 360, label: "360°" }
                    ]}
                    mb="xs"
                />
            </Stack>

            {error && <Text size="xs" c="red">{error}</Text>}
        </Stack>
    );
}
